<template>
  <div>
    <a
      :href="'/products/' + product.handle + '?variant=' + selectedVariant.id"
      class="block mb-2"
    >
      <img-yay
        ref="img"
        :src="imageSrc"
        :width="458"
        :height="330"
        :lazy="false"
        :sizes="imageSizes"
        :alt="strings.collection.products.image_alt"
        class="object-fit min w-full mb-4"
        @mouseover="showSneakPeak = true"
        @mouseleave="showSneakPeak = false"
      />
      <div class="flex justify-between">
        <h3 class="font-medium leading-6 tracking-wide">
          {{ product.title }}
        </h3>
        <div class="text-sm mt-0.5">{{ strings.product.price.currency_symbol }}{{ formatPrice(product.price) }}</div>
      </div>
    </a>
    <collection-product-swatches
      :product="product"
      @hoveron="hoverOn($event)"
      @hoveroff="hoverOff($event)"
      @click-swatch="swatchClicked($event)"
    />
  </div>
</template>

<script>
import useBreakpoints from '../composables/useBreakpoints'
const { screenType } = useBreakpoints()
const TOGGLE_PRELOAD_LIMIT = 12

export default {
  props: {
    product: { type: Object, required: true },
    productIndex: { type: Number, default: 0 },
    selectedColumns: { type: Number, default: 3 },
    selectedMobileColumns: { type: Number, default: 1 },
  },
  data() {
    let variant = this.product.variants[0]
    let images = []
    let showSneakPeak = false
    let selectedVariant = this.product.variants[0]
    this.product.variants.forEach(v => {
      images[v.title] = this.variantImageSrc(v)
    })
    return {
      variant,
      images,
      showSneakPeak,
      selectedVariant,
      strings: window.themeVariables.i18n,
    }
  },
  computed: {
    variantTitle() {
      return this.variant.title
    },
    imageSrc() {
      if (this.showSneakPeak && this.sneakPeak) {
        return this.sneakPeak
      }
      return this.images[this.variant.title]
    },
    sneakPeak() {
      return this.product.sneakPeak
    },
    imageSizes() {
      let desktopColumns = this.selectedColumns
      if (![2, 3, 4].includes(desktopColumns)) desktopColumns = 3
      let mobileColumns = this.selectedMobileColumns
      if (![1, 2].includes(mobileColumns)) mobileColumns = 1
      const desktopGapPx = (desktopColumns - 1) * 48
      const desktopSize = `calc((min(100vw, 1540px) - 128px - ${desktopGapPx}px) / ${desktopColumns})`
      const mobileGapPx = (mobileColumns - 1) * 16
      const mobileSize = `calc((min(100vw, 1540px) - 32px - ${mobileGapPx}px) / ${mobileColumns})`
      const smallTabletGapPx = (mobileColumns - 1) * 32
      const smallTabletSize = `calc((min(100vw, 1540px) - 64px - ${smallTabletGapPx}px) / ${mobileColumns})`
      const tabletGapPx = (mobileColumns - 1) * 48
      const tabletSize = `calc((min(100vw, 1540px) - 176px - ${tabletGapPx}px) / ${mobileColumns})`
      return `(min-width: 1024px) ${desktopSize}, (min-width: 768px) ${tabletSize}, (min-width: 640px) ${smallTabletSize}, ${mobileSize}`
    },
  },
  watch: {
    sneakPeak() {
      this.preloadImages([this.sneakPeak], true)
    },
    selectedColumns(newVal, oldVal) {
      if (this.shouldPreloadOnUpsizeToggle(newVal, oldVal)) {
        this.preloadImages(this.images)
      }
    },
  },
  mounted() {
    window.addEventListener('pageshow', this.handlePageShow)
  },
  beforeUnmount() {
    window.removeEventListener('pageshow', this.handlePageShow)
  },
  created() {
    this.preloadImages(this.images)
  },
  methods: {
    handlePageShow(event) {
      if (event.persisted) {
        this.$nextTick(() => {
          const images = this.$el.querySelectorAll('img')
          images.forEach(img => {
            const currentSrc = img.src
            if (currentSrc) {
              img.src = ''
              img.src = currentSrc
            }
          })
          this.showSneakPeak = false
          this.variant = this.product.variants[0]
          this.selectedVariant = this.product.variants[0]
        })
      }
    },
    variantImageSrc(variant) {
      let result = variant.image
      if (variant.featured_image) {
        result = variant.featured_image.src
      }
      if (!result) {
        result = this.product.featured_image
      }
      return result
    },
    preloadImages(images, high_priority = false) {
      if (screen.width > 480) {
        let result = []
        this.preloadSizes().forEach(size => {
          result = result.concat(
            Object.values(images).map(src => {
              return src ? this.formatImageSize(src, `_${size}_crop_center`) : null
            }),
          )
        })
        this.$store.commit('config/preloadImages', { images: result, high_priority })
      }
    },
    preloadSizes() {
      if (window.innerWidth < 1024) return ['458x330', '687x495']
      if (this.selectedColumns === 2) return ['687x495', '916x660', '1374x990']
      if (this.selectedColumns === 4) return ['458x330', '687x495']
      return ['458x330', '687x495', '916x660']
    },
    shouldPreloadOnUpsizeToggle(newVal, oldVal) {
      if (window.innerWidth < 1024) return false
      if (!Number.isInteger(newVal) || !Number.isInteger(oldVal)) return false
      if (this.productIndex >= TOGGLE_PRELOAD_LIMIT) return false
      return newVal < oldVal
    },
    hoverOn($event) {
      this.variant = $event
    },
    hoverOff() {
      if (screenType.value === 'xs' || screenType.value === 'md') {
        this.variant = this.selectedVariant || this.product.variants[0]
      } else {
        this.variant = this.product.variants[0]
      }
    },
    swatchClicked($event) {
      if (screenType.value === 'xs' || screenType.value === 'md') {
        this.showSneakPeak = false
        this.variant = $event.variant
        this.selectedVariant = $event.variant
      } else {
        window.location.href = $event.variantUrl
      }
    },
    variantUrl() {
      '/products/' + this.selectedVariant.handle
    },
  },
}
</script>